import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  ProductRequest,
  ProductResponse,
  ProductSearchParams,
  ProductPriceHistoryResponse,
  CurrencyResponse,
  PageResponse,
  ProductImageUploadResponse,
} from '../models/producto.model';
// Inc 28: el timeline reutiliza el shape de auditoría (EntityAuditEventResponse ≡ AuditLogResponse)
// y la página estilo audit ({ content, page, size, totalElements, totalPages, hasNext, hasPrevious }).
import {
  AuditLogResponse,
  PageResponse as TimelinePageResponse,
} from '../../audit/models/audit.model';

@Injectable({
  providedIn: 'root',
})
export class ProductoApiService {
  private readonly apiUrl = `${environment.apiUrl}api`;

  constructor(private http: HttpClient) {}

  searchProduct(
    params: ProductSearchParams,
  ): Observable<PageResponse<ProductResponse>> {
    let httpParams = new HttpParams().set(
      'businessId',
      params.businessId.toString(),
    );

    if (params.idCategory !== undefined)
      httpParams = httpParams.set('idCategory', params.idCategory.toString());
    if (params.type) httpParams = httpParams.set('type', params.type);
    if (params.subtype) httpParams = httpParams.set('subtype', params.subtype);
    if (params.searchName)
      httpParams = httpParams.set('searchName', params.searchName);
    if (params.searchSku)
      httpParams = httpParams.set('searchSku', params.searchSku);
    if (params.minPrice !== undefined)
      httpParams = httpParams.set('minPrice', params.minPrice.toString());
    if (params.maxPrice !== undefined)
      httpParams = httpParams.set('maxPrice', params.maxPrice.toString());
    if (params.isActive !== undefined)
      httpParams = httpParams.set('isActive', params.isActive.toString());
    if (params.isRecurring !== undefined)
      httpParams = httpParams.set('isRecurring', params.isRecurring.toString());
    if (params.billingCycle)
      httpParams = httpParams.set('billingCycle', params.billingCycle);
    if (params.hasVariants !== undefined)
      httpParams = httpParams.set('hasVariants', params.hasVariants.toString());
    if (params.usesTierPricing !== undefined)
      httpParams = httpParams.set(
        'usesTierPricing',
        params.usesTierPricing.toString(),
      );
    if (params.page !== undefined)
      httpParams = httpParams.set('page', params.page.toString());
    if (params.size !== undefined)
      httpParams = httpParams.set('size', params.size.toString());
    if (params.sortBy) httpParams = httpParams.set('sortBy', params.sortBy);
    if (params.sortDirection)
      httpParams = httpParams.set('sortDirection', params.sortDirection);
    if (params.status) httpParams = httpParams.set('status', params.status);

    return this.http.get<PageResponse<ProductResponse>>(
      `${this.apiUrl}/products`,
      { params: httpParams },
    );
  }

  getProductById(id: number, businessId: number): Observable<ProductResponse> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get<ProductResponse>(`${this.apiUrl}/products/${id}`, {
      params,
    });
  }

  createProduct(product: ProductRequest): Observable<ProductResponse> {
    return this.http.post<ProductResponse>(`${this.apiUrl}/products`, product);
  }

  uploadProductImage(file: File): Observable<ProductImageUploadResponse> {
    const form = new FormData();
    form.append('file', file, file.name);
    const params = new HttpParams()
      .set('category', 'PRODUCT_IMAGE')
      .set('isPublic', 'true');
    return this.http.post<ProductImageUploadResponse>(
      `${environment.apiUrl}api/files/upload`,
      form,
      { params },
    );
  }

  updateProduct(
    id: number,
    businessId: number,
    product: ProductRequest,
  ): Observable<ProductResponse> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.put<ProductResponse>(
      `${this.apiUrl}/products/${id}`,
      product,
      { params },
    );
  }

  deleteProduct(id: number, businessId: number): Observable<void> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.delete<void>(`${this.apiUrl}/products/${id}`, { params });
  }

  getPriceHistory(
    productId: number,
    page: number,
    size: number,
  ): Observable<PageResponse<ProductPriceHistoryResponse>> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
    return this.http.get<PageResponse<ProductPriceHistoryResponse>>(
      `${this.apiUrl}/products/${productId}/price-history`,
      { params },
    );
  }

  getCurrencies(): Observable<CurrencyResponse[]> {
    return this.http.get<CurrencyResponse[]>(`${this.apiUrl}/currencies`);
  }

  // ── Inc 22: múltiples categorías por producto ──

  /** GET /api/products/{id}/categories → number[] con los ids de categoría asociados. */
  getProductCategories(id: number, businessId: number): Observable<number[]> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get<number[]>(`${this.apiUrl}/products/${id}/categories`, {
      params,
    });
  }

  /** PUT /api/products/{id}/categories { categoryIds:[...] } → number[] (arreglo final). [] desasocia. */
  setProductCategories(
    id: number,
    businessId: number,
    categoryIds: number[],
  ): Observable<number[]> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.put<number[]>(
      `${this.apiUrl}/products/${id}/categories`,
      { categoryIds },
      { params },
    );
  }

  // ── Inc 28: timeline propio del producto (products:VIEW, ya NO audit:VIEW) ──

  /** GET /api/products/{id}/timeline → PageResponse<EntityAuditEventResponse> (desc por fecha). */
  getProductTimeline(
    id: number,
    page: number,
    size: number,
  ): Observable<TimelinePageResponse<AuditLogResponse>> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
    return this.http.get<TimelinePageResponse<AuditLogResponse>>(
      `${this.apiUrl}/products/${id}/timeline`,
      { params },
    );
  }
}
